import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    WatchSignals,
} from "../components/AnalyticsShared";
import { AnalyticsSeriesChart } from "../components/AnalyticsSeriesChart";
import { useAsyncBlock } from "../hooks";
import { bidsOrderConversionApi } from "../../bidsApi";
import { BidOrderConversionReport } from "../../../../models/bidAnalytics";
import { formatInt, formatMoneyKopecks, formatRatePercent, toNum } from "../format";
import { rollupSeries } from "../rollup";
import { Game } from "../../../../models/games";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function OrderConversionTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;

    const [state, refresh] = useAsyncBlock<BidOrderConversionReport>(
        () =>
            bidsOrderConversionApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
            }),
        isActive,
        [filters.from, filters.to, filters.gameId]
    );

    const data = state.data?.totals;
    const chartData = React.useMemo(() => {
        const rolled = rollupSeries(state.data?.series ?? [], filters.granularity, {
            sumKeys: [
                "bidsCreated",
                "bidsWithOrders",
                "ordersFromBids",
                "completedOrdersFromBids",
                "failedOrdersFromBids",
                "ordersSum",
                "completedOrdersSum",
            ],
            rateDefs: [
                {
                    rateKey: "bidToOrderRate",
                    numerator: "bidsWithOrders",
                    denominator: "bidsCreated",
                },
            ],
        });
        return rolled.map((p) => ({
            ...p,
            bidToOrderPct: toNum(p.bidToOrderRate) * 100,
        }));
    }, [state.data?.series, filters.granularity]);

    const isEmpty = data != null && data.bidsCreated === 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod
                showGranularity
            />

            <AnalyticsCard
                title="Превращаются ли заявки в сделки"
                subtitle="Когорта: заявки периода и их ордера (по дате создания заявки). График — динамика конверсии."
                snapshotLabel="period"
                howToRead={
                    <List size="sm" marker="disc">
                        <ListItem>
                            <Typography level="body-sm">
                                Берём заявки, созданные в периоде. Ордера этих заявок считаем в том же
                                бакете (по Created заявки), даже если ордер появился позже.
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                <strong>Конверсия %</strong> = заявки с ≥1 ордером / созданные заявки
                                бакета (не больше 100%).
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                Из ордеров когорты — сколько успешных и провальных; суммы в ₽
                            </Typography>
                        </ListItem>
                    </List>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "% конверсии заявка → ордер",
                                ifGrows: "спрос чаще превращается в реальные ордера.",
                                ifFalls: "заявки реже порождают сделки (нет офферов, цены не сходятся, сбои матчинга).",
                            },
                            {
                                signal: "Доля провальных ордеров относительно успешных",
                                growsIsGood: false,
                                ifGrows: "сделки стартуют, но ломаются (трейд, бот, отмена); смотрите ордера.",
                                ifFalls: "исполнение ордеров стабильнее.",
                            },
                            {
                                signal: "Сумма успешных ордеров",
                                ifGrows: "заявки дают больше реальной выручки через ордера.",
                                ifFalls: "при том же числе заявок денежный результат слабее.",
                            },
                        ]}
                    />
                }
            >
                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && isEmpty && <EmptyState />}
                {!state.loading && !state.error && data && !isEmpty && (
                    <>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <MetricHint label="Заявок создано" value={formatInt(data.bidsCreated)} />
                            <MetricHint
                                label="Заявок с ордерами"
                                value={formatInt(data.bidsWithOrders)}
                                color="primary"
                            />
                            <MetricHint
                                label="Конверсия заявка → ордер"
                                value={formatRatePercent(data.bidToOrderRate)}
                                color="success"
                            />
                            <MetricHint label="Ордеров из заявок" value={formatInt(data.ordersFromBids)} />
                            <MetricHint
                                label="Успешных ордеров"
                                value={formatInt(data.completedOrdersFromBids)}
                                color="success"
                            />
                            <MetricHint
                                label="Провальных ордеров"
                                value={formatInt(data.failedOrdersFromBids)}
                                color="danger"
                            />
                            <MetricHint label="Сумма ордеров" value={formatMoneyKopecks(data.ordersSum)} />
                            <MetricHint
                                label="Сумма успешных"
                                value={formatMoneyKopecks(data.completedOrdersSum)}
                                color="success"
                            />
                        </Box>

                        <Typography level="title-sm">Динамика</Typography>
                        <AnalyticsSeriesChart
                            dataset={chartData}
                            granularity={filters.granularity}
                            series={[
                                {
                                    dataKey: "bidToOrderPct",
                                    label: "Конверсия %",
                                    color: "#2e7d32",
                                    valueFormatter: (v) =>
                                        v == null
                                            ? "—"
                                            : `${v.toLocaleString("ru-RU", { maximumFractionDigits: 1 })}%`,
                                },
                                { dataKey: "bidsCreated", label: "Заявок", color: "#455a64" },
                                { dataKey: "bidsWithOrders", label: "С ордерами", color: "#1976d2" },
                                { dataKey: "failedOrdersFromBids", label: "Провальных ордеров", color: "#c62828" },
                            ]}
                        />
                    </>
                )}
            </AnalyticsCard>
        </Box>
    );
}
